import { useEffect, useRef, useState } from 'react';
import { CircularProgress, Grid } from '@mui/material';
import { Position } from 'geojson';
import { coordEach, feature, flatten } from '@turf/turf';

import useGoogleMapsApiLoader from 'src/infrastructure/google/useGoogleMapsApiLoader';
import { GOOGLE_MAPS_MAP_ID_ADMI_MAP } from 'src/appConfig';
import { generateColorHashFromString } from 'src/common/generateColorHashFromString';
import {
    setLocation,
    loadPotentialBuildings,
    setSelectedBuildingMetadata,
    setHighlightedBuilding,
    removeBuildingMetadata,
} from '../pv-analysis.slice';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import useDebouncer from 'src/common/useDebouncer';

export interface TakeScreenshotOptions {
    blob: Blob;
    center: Position;
    zoom: number;
}

export interface MapOptions {
    center: Position;
    zoom: number;
}

const LOAD_BUILDINGS_DEBOUNCE_MS = 1000;
const LOAD_BUILDINGS_RADIUS = 250;

export default function MetaDataMap() {
    const { location, buildingsMetadata, isImportBuildingLoading, selectedBuildingMetadata, highlightedBuilding } =
        useAppSelector((state) => state.pvAnalysis);
    const dispatch = useAppDispatch();

    const [map, setMap] = useState<google.maps.Map | undefined>();
    const [marker, setMarker] = useState<google.maps.marker.AdvancedMarkerElement>();
    const [geocoder, setGeocoder] = useState<google.maps.Geocoder>();

    const [polygons, setPolygons] = useState<{
        [key: string]: google.maps.Polygon;
    }>({});

    const loader = useGoogleMapsApiLoader();

    const mapRef = useRef(null);
    const locationRef = useRef(location);

    const loadBuildingsDebouncer = useDebouncer();

    useEffect(() => {
        loadMap();
    }, []);

    useEffect(() => {
        console.debug('Map.location changed');
        refreshCenter();
        refreshPolygons();
        refreshSearchMarker();
    }, [location, buildingsMetadata]);

    useEffect(() => {
        if (map !== undefined && geocoder !== undefined) {
            google.maps.event.addListener(map, 'dragend', () => dragend(map, geocoder));
        }
    }, [map, geocoder]);

    useEffect(() => {
        locationRef.current = location;

        if (!location) {
            return;
        }

        loadBuildingsDebouncer(() => {
            console.debug('RealEstateData.location', location);
            dispatch(
                loadPotentialBuildings({
                    location,
                    radius: LOAD_BUILDINGS_RADIUS,
                }),
            );
        }, LOAD_BUILDINGS_DEBOUNCE_MS);
    }, [location]);

    useEffect(() => {
        refreshPolygonsHighlight();
    }, [highlightedBuilding]);

    useEffect(() => {
        refreshPolygons();
    }, [selectedBuildingMetadata]);

    async function loadMap() {
        console.debug('loadMap');

        const mapOptions: google.maps.MapOptions = {
            mapId: GOOGLE_MAPS_MAP_ID_ADMI_MAP,
            mapTypeId: 'satellite',
            mapTypeControl: false,
            tilt: 0,
            tiltInteractionEnabled: false,
            streetViewControl: false,
            fullscreenControl: false,
        };
        const mapElement = document.getElementById('map');
        if (mapElement !== null) {
            const { Map } = (await loader.importLibrary('maps')) as google.maps.MapsLibrary;

            // Since this is an async function, the location property might be a stale value
            // We therefore use a ref to access the latest version.
            if (locationRef.current) {
                mapOptions.center = { lat: locationRef.current.lat, lng: locationRef.current.lng };
                mapOptions.zoom = locationRef.current.zoom ?? 18;
            }

            setMap(new Map(mapElement, mapOptions));

            const { Geocoder } = (await loader.importLibrary('geocoding')) as google.maps.GeocodingLibrary;
            setGeocoder(new Geocoder());
        }
    }

    async function refreshPolygons() {
        console.debug('refreshPolygons');

        if (map !== undefined && buildingsMetadata !== undefined) {
            // Clear existing polygons
            console.log('buildingsMetadata', buildingsMetadata);
            Object.keys(polygons).forEach((key) => polygons[key].setMap(null));
            setPolygons({});

            // Define the color for selected buildings
            const selectedColor = 'f5cb5a';

            // Iterate over all buildings
            buildingsMetadata.forEach((buildingMetadata) => {
                // Determine the color: selected buildings use selectedColor, others get unique colors
                const color =
                    selectedBuildingMetadata?.code === buildingMetadata.code
                        ? selectedColor
                        : generateColorHashFromString(`building_${buildingMetadata.code}`);

                console.debug('building geometry', buildingMetadata.geometry);

                const buildingsPolygons = flatten(feature(buildingMetadata.geometry));
                buildingsPolygons.features.forEach((feature) => {
                    console.debug('feature', feature);

                    const polygonData: { lat: number; lng: number }[] = [];
                    coordEach(feature, (coord) => {
                        polygonData.push({ lat: coord[1], lng: coord[0] });
                    });

                    // Create a new polygon with the determined color
                    const polygon = new google.maps.Polygon({
                        paths: polygonData,
                        strokeColor: `#${color}`,
                        strokeOpacity: 1,
                        strokeWeight: 1,
                        fillColor: `#${color}`,
                        fillOpacity: highlightedBuilding === buildingMetadata.code ? 1 : 0.5,
                    });

                    google.maps.event.addListener(polygon, 'click', () => {
                        if (buildingMetadata.code !== selectedBuildingMetadata?.code) {
                            dispatch(setSelectedBuildingMetadata(buildingMetadata));
                        }
                        // Refresh polygons to update selected buildings' colors
                        refreshPolygons();
                    });
                    google.maps.event.addListener(polygon, 'mouseover', () =>
                        dispatch(setHighlightedBuilding(buildingMetadata.code)),
                    );
                    google.maps.event.addListener(polygon, 'mouseout', () => dispatch(setHighlightedBuilding('')));
                    google.maps.event.addListener(polygon, 'rightclick', () => {
                        dispatch(removeBuildingMetadata(buildingMetadata.code));
                    });

                    // Display the polygon on the map
                    polygon.setMap(map);

                    // Update state with the new polygon
                    const statePolygons = polygons;
                    statePolygons[buildingMetadata.code] = polygon;
                    setPolygons(statePolygons);
                });
            });
        }
    }

    async function dragend(map: google.maps.Map, geocoder: google.maps.Geocoder): Promise<void> {
        console.debug('dragend', map, geocoder);
        if (map !== undefined && geocoder !== undefined) {
            try {
                const center = map.getCenter();
                if (!center) {
                    throw new Error('Map center is not defined');
                }

                const geocoderResponse: google.maps.GeocoderResponse = await geocoder.geocode({
                    location: center,
                });
                console.debug('geocoderResponse', geocoderResponse);

                dispatch(
                    setLocation({
                        address: geocoderResponse.results[0].formatted_address,
                        lat: center.lat(),
                        lng: center.lng(),
                    }),
                );
            } catch (e) {
                console.error('Geocoder failed due to: ' + e);
            }
        }
    }

    function refreshCenter() {
        console.debug('refreshCenter', location);

        if (map !== undefined && location) {
            const centerLatLng = new google.maps.LatLng(location.lat, location.lng);
            map.setCenter(centerLatLng);

            if (location.zoom !== undefined) {
                map.setZoom(location.zoom);
            }
        }
    }

    async function refreshSearchMarker(): Promise<void> {
        console.debug('refreshSearchMarker', location);

        if (map !== undefined && location) {
            const { AdvancedMarkerElement } = (await loader.importLibrary('marker')) as google.maps.MarkerLibrary;
            if (marker !== undefined) {
                marker.map = null;
                setMarker(marker);
            }

            setMarker(
                new AdvancedMarkerElement({
                    position: { lat: location.lat, lng: location.lng },
                    map,
                    title: location.address + '\n' + location.lat + ',' + location.lng,
                }),
            );
        }
    }

    function refreshPolygonsHighlight(): void {
        console.debug('refreshPolygonsHighlight');

        if (map !== undefined && polygons !== undefined) {
            Object.keys(polygons).forEach((key) => {
                polygons[key].setOptions({
                    fillOpacity: key == highlightedBuilding ? 1 : 0.5,
                });
            });
        }
    }

    return (
        <Grid
            size={12}
            container
            className="map"
            sx={{ justifyContent: 'center' }}
            style={{ height: '95%', width: '100%' }}
        >
            <Grid style={{ height: '95%', width: '100%' }}>
                <div ref={mapRef} id="map" style={{ height: '100%', width: '100%' }} />

                {isImportBuildingLoading && (
                    <div style={{ position: 'absolute', top: 8, left: 8 }}>
                        <CircularProgress />
                    </div>
                )}
            </Grid>
        </Grid>
    );
}
